import {useState, useRef, useCallback, useEffect, useMemo} from 'react'

const usePrizeEditors = (initialPrizes = []) => {
	// Initialize with at least one empty prize if none provided
	const [prizes, setPrizes] = useState(() => {
		if (Array.isArray(initialPrizes) && initialPrizes.length > 0) {
			return initialPrizes.map((prize, index) => ({
				id: `prize-${index}`,
				content: prize || '',
			}))
		}
		return [{id: 'prize-0', content: ''}]
	})

	// Store refs for each editor
	const editorRefs = useRef({})

	// Track if we're initializing from external data
	const [isInitializing, setIsInitializing] = useState(false)

	// Get ref for specific prize editor
	const getPrizeEditorRef = useCallback((prizeId) => {
		if (!editorRefs.current[prizeId]) {
			editorRefs.current[prizeId] = {
				current: null,
			}
		}
		return editorRefs.current[prizeId]
	}, [])

	const getAllPrizeEditorRefs = useCallback(() => {
		return editorRefs.current
	}, [])

	const prizeList = useMemo(() => {
		return Object.values(getAllPrizeEditorRefs()).map((ref) =>
			ref.current.getContent()
		)
	}, [prizes])

	// Update editor content when prizes change (for initialization)
	useEffect(() => {
		if (isInitializing) {
			// Use setTimeout to ensure editors are mounted
			const timer = setTimeout(() => {
				prizes.forEach((prize) => {
					const editorRef = editorRefs.current[prize.id]
					if (editorRef?.current?.setContent && prize.content) {
						editorRef.current.setContent(prize.content)
					}
				})
				setIsInitializing(false)
			}, 100) // Small delay to ensure editors are mounted

			return () => clearTimeout(timer)
		}
	}, [prizes, isInitializing])

	// Add new prize
	const addPrize = useCallback(() => {
		const newPrizeId = `prize-${Date.now()}`
		setPrizes((prev) => [...prev, {id: newPrizeId, content: ''}])
	}, [])

	// Remove prize by id
	const removePrize = useCallback((prizeId) => {
		setPrizes((prev) => {
			const filtered = prev.filter((prize) => prize.id !== prizeId)
			// Always keep at least one prize
			return filtered.length === 0 ? [{id: 'prize-0', content: ''}] : filtered
		})

		// Clean up the ref
		if (editorRefs.current[prizeId]) {
			delete editorRefs.current[prizeId]
		}
	}, [])

	// Update prize content
	const updatePrizeContent = useCallback((prizeId, content) => {
		setPrizes((prev) =>
			prev.map((prize) => (prize.id === prizeId ? {...prize, content} : prize))
		)
	}, [])

	// Get all prize contents as array
	const getAllPrizeContents = useCallback(() => {
		const results = prizes.map((prize) => {
			const ref = editorRefs.current[prize.id]
			if (ref?.current?.getContent) {
				return ref.current.getContent()
			}
			return prize.content
		})
		return results
	}, [prizes])

	// Get all prize contents as array for form submission
	const getPrizeEditorValue = useCallback(() => {
		return getAllPrizeContents()
	}, [getAllPrizeContents])

	// Reset all prizes
	const resetPrizes = useCallback(() => {
		setPrizes([{id: 'prize-0', content: ''}])
		editorRefs.current = {}
	}, [])

	// Set prizes from external data
	const setPrizesFromData = useCallback((prizeData) => {
		if (Array.isArray(prizeData)) {
			const newPrizes = prizeData.map((prize, index) => ({
				id: `prize-${index}`,
				content: prize || '',
			}))
			setPrizes(
				newPrizes.length > 0 ? newPrizes : [{id: 'prize-0', content: ''}]
			)
			// Trigger initialization if we have content
			if (newPrizes.some((prize) => prize.content)) {
				setIsInitializing(true)
			}
		} else if (typeof prizeData === 'string') {
			// Handle legacy single prize format
			setPrizes([{id: 'prize-0', content: prizeData}])
			if (prizeData) {
				setIsInitializing(true)
			}
		}
	}, [])

	return {
		prizes,
		prizeList,
		addPrize,
		removePrize,
		updatePrizeContent,
		getAllPrizeContents,
		getPrizeEditorValue,
		getPrizeEditorRef,
		getAllPrizeEditorRefs,
		resetPrizes,
		setPrizesFromData,
	}
}

export default usePrizeEditors
